import { z } from "zod";
import { VIDEO_STYLES, VideoStyle, piecesStyle } from "./scene.js";

export const ContentKind = z.enum(["post", "video"]);
export const Format = z.enum(["square", "portrait", "story", "landscape"]);
export type Format = z.infer<typeof Format>;

export const FORMAT_SIZE: Record<Format, { width: number; height: number }> = {
  square: { width: 1080, height: 1080 },
  portrait: { width: 1080, height: 1350 },
  story: { width: 1080, height: 1920 },
  landscape: { width: 1920, height: 1080 },
};

/** What the Studio chat must learn before generation (03). */
export const Brief = z.object({
  idea: z.string().min(3).max(500),
  kind: ContentKind,
  format: Format,
  sourceMediaIds: z.array(z.uuid()).max(30),
  voice: z.object({ enabled: z.boolean(), voiceId: z.string().max(64).optional() }),
  /** An animation laid over the video (`ANIMATIONS`): one from the catalogue, or none. Videos only; a post has nothing to move. */
  animation: z.object({ enabled: z.boolean(), animationId: z.string().max(64).optional() }).optional(),
  /**
   * What the video's shots are made of (ADR 0050): pictures (photos, or generated when none were chosen), or drawn
   * scenes from the renderer's library, which cost nothing to make. Unset means pictures. Videos only.
   */
  look: z.enum(["pictures", "drawn"]).optional(),
  /**
   * How a drawn video looks as a whole (`VIDEO_STYLES`): set by a gallery pick or on the plan. Unset means `clean`.
   * Read through `drawnStyle`, never directly: it is only worn while the look is drawn.
   */
  style: VideoStyle.optional(),
  /** Music drawn by code under the video, in its style's feel (ADR 0054). Unset means on; videos only. */
  music: z.boolean().optional(),
  onScreenText: z.string().max(120).optional(),
  durationMs: z.number().int().min(5_000).max(60_000).optional(),
});
export type Brief = z.infer<typeof Brief>;

export const BriefDraft = Brief.partial();
export type BriefDraft = z.infer<typeof BriefDraft>;

/** What the chat asks for each field, as the API's guided flow (apps/api/src/routes/projects.ts) words it. */
export const BRIEF_QUESTIONS: Record<keyof Brief, string> = {
  idea: "What is this about? One sentence is enough.",
  kind: "A post (one image) or a short video?",
  format: "Where will it go? That sets the shape.",
  sourceMediaIds: "Which of your photos should I use? Pick a few, or none and I'll generate.",
  voice: "Narration on or off?",
  animation: "An animation on top? A motion-graphics flourish, made in After Effects, over the opening shot.",
  look: "Pictures, or drawn? Drawn scenes cost nothing to make.",
  // Named from the styles themselves (2026-09-29): the list here had fallen behind them once already.
  style: `Which style? ${VIDEO_STYLES.slice(0, -1).map((s) => s.name).join(", ")} or ${VIDEO_STYLES.at(-1)!.name}.`,
  music: "Music under it, or none?",
  onScreenText: "Anything to say on screen? An offer, a phone number, opening hours.",
  durationMs: "How long?",
};

/**
 * Fields still missing, in the order the chat asks them. The animation is a video's question: a post has nothing to
 * move, and a drawn video of pieces opens on one, which an animation is never laid over (ADR 0058, 0061).
 */
export function briefMissing(draft: BriefDraft): Array<keyof Brief> {
  const animated = draft.kind === "video" && !showreelVideo(draft);
  const order: Array<keyof Brief> = ["idea", "kind", "format", "sourceMediaIds", "voice", ...(animated ? (["animation"] as const) : [])];
  return order.filter((k) => draft[k] === undefined);
}

/**
 * The style a video is made in, decided here and nowhere else: the brief's own while the video is drawn, and none when
 * it is made of pictures. The plan's look toggle leaves a style picked on a drawn plan in the brief, so switching back
 * to drawn finds it again; a pictures video keeps the brand's plain caption chip and clean's music whatever it says
 * (found 2026-09-28: a photo video wore a neon chip and neon's music). Unset on a drawn video means `clean`.
 */
export const drawnStyle = (draft: Pick<BriefDraft, "look" | "style">): VideoStyle | undefined => (draft.look === "drawn" ? draft.style : undefined);

/**
 * A drawn video of pieces, in any of the piece styles (`PIECE_STYLES`): every scene a showreel piece (ADR 0058). The
 * name was kept when three more styles came to be made of pieces (2026-09-29, ADR 0061), as was the script's
 * `showreel` field it sets, so the API and the web app did not churn for a word; both now mean "a video of pieces".
 */
export const showreelVideo = (draft: Pick<BriefDraft, "look" | "style">) => piecesStyle(drawnStyle(draft));
